import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { API_BASE, STORAGE_KEYS } from '@/settings'
import { getToken, getUserId, clearAuth } from '@/utils/auth'

/** 供页面拼接导出/下载等直链使用（生成页面统一从 @/utils/request 引入） */
export { API_BASE } from '@/settings'

/**
 * ============================================================================
 * 请求层 —— 严格对齐旧工程 static/js/common/common.js 的 ajaxData / ajaxSubmitForm 语义
 * ============================================================================
 *
 * 旧工程接口契约（务必保持不变，后端无需任何改动）：
 *   1) 一律 POST，Content-Type: application/x-www-form-urlencoded
 *   2) 请求头携带 token / userid（见旧 getHeaderParam）
 *   3) 响应体固定结构：
 *        { success: boolean, result: any, totalCount: number, errorCode: string, errorMsg: string }
 *   4) 业务成功时，回调拿到的是 response.result（旧 globalAjaxSuccess）
 *   5) 列表接口：rows = response.result，total = response.totalCount（旧 resTableHandler）
 *   6) 错误码约定：
 *        errorCode '401' -> alert 后刷新页面（登录态失效）
 *        HTTP 300 -> 用户信息验证失败，跳登录
 *        HTTP 305 -> 无权限操作
 *        HTTP 308 -> 跳登录
 *        HTTP 403 -> 系统繁忙
 *
 * 迁移注意：axios 默认把对象序列化为 JSON。这里显式使用 URLSearchParams，
 * 以复现 jQuery $.ajax 的 form-urlencoded 行为，避免后端解析失败。
 */

/** 旧接口统一响应结构 */
export interface LegacyResponse<T = any> {
  success: boolean
  result: T
  totalCount?: number
  errorCode?: string
  errorMsg?: string
}

/** 分页查询结果，对齐 bootstrap-table 的 rows/total */
export interface PageResult<T = any> {
  rows: T[]
  total: number
}

/** 旧 bootstrap-table 的 queryParams 结构 */
export interface TableQuery {
  pageSize: number
  pageIndex: number
  order?: string
  orderName?: string
  [key: string]: any
}

function toFormData(data: any): URLSearchParams {
  const sp = new URLSearchParams()
  if (data == null) return sp
  if (data instanceof URLSearchParams) return data
  Object.keys(data).forEach((k) => {
    const v = data[k]
    if (v === undefined || v === null) return
    if (Array.isArray(v)) {
      // 旧工程数组参数以逗号拼接传递
      sp.append(k, v.join(','))
    } else if (typeof v === 'object') {
      sp.append(k, JSON.stringify(v))
    } else {
      sp.append(k, String(v))
    }
  })
  return sp
}

const service: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  withCredentials: false
})

/** 请求拦截：补 token / userid 头 + form-urlencoded 序列化 */
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken()
    if (token) config.headers.set('token', token)
    const userId = getUserId()
    if (userId) config.headers.set('userid', String(userId))

    // 非 GET 请求统一转 form-urlencoded
    const method = (config.method || 'post').toLowerCase()
    if (method !== 'get' && !(config.data instanceof FormData)) {
      config.headers.set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8')
      config.data = toFormData(config.data)
    }
    return config
  },
  (error) => Promise.reject(error)
)

/** 响应拦截：剥离外层包装，对齐旧 globalAjaxSuccess */
service.interceptors.response.use(
  (response: AxiosResponse<LegacyResponse>) => {
    const res = response.data

    // 二进制流（导出/下载）直接返回
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response as any
    }

    // 后端未按约定包装时直接返回原始体，避免误判
    if (res == null || typeof res !== 'object' || !('success' in res)) {
      return res as any
    }

    // fetchTable 需要读取 totalCount，带上此标记时保留完整包装
    if ((response.config.headers as any)?.['X-Keep-Wrapper']) {
      return res as any
    }

    if (res.success === false) {
      if (res.errorCode === '401' || res.errorCode === '300') {
        ElMessage.error(res.errorMsg || '登录状态已失效，请重新登录')
        clearAuth()
        setTimeout(() => {
          window.location.href = '/login'
        }, 800)
        return Promise.reject(new Error(res.errorMsg || 'unauthorized'))
      }
      ElMessage.error(res.errorMsg || '操作失败')
      return Promise.reject(new Error(res.errorMsg || 'request failed'))
    }

    // 成功：把 result 直接作为返回值，复现旧回调体验
    return res.result as any
  },
  (error) => {
    const status = error?.response?.status
    if (status === 300) {
      clearAuth()
      window.location.href = '/login'
    } else if (status === 305) {
      ElMessage.error('您无权限操作该处理。')
    } else if (status === 308) {
      clearAuth()
      window.location.href = '/login'
    } else if (status === 403) {
      ElMessage.error('系统忙，请稍后再试。')
    } else if (error?.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请稍后再试。')
    } else {
      ElMessage.error('系统出现异常，请稍后再试。')
    }
    return Promise.reject(error)
  }
)

/**
 * 旧 ajaxData 的等价实现。
 * 用法：const list = await request('/banksmanager/queryBanksList', { pageIndex: 0 })
 */
export function request<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  return service.post(url, data, config) as unknown as Promise<T>
}

/** GET 请求（旧工程少量使用 ajaxHtml / 静态 json） */
export function get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
  return service.get(url, { params, ...config }) as unknown as Promise<T>
}

/**
 * 列表查询：等价于旧 bootstrap-table 的 url + queryParams + responseHandler 三件套。
 * 返回 { rows, total }，与旧 resTableHandler 完全一致。
 *
 * 注意：普通 request() 会被响应拦截器剥成 result，totalCount 会丢；
 * 这里通过 X-Keep-Wrapper 标记保留 { result, totalCount } 包装以支持服务端分页。
 */
export async function fetchTable<T = any>(
  url: string,
  query: TableQuery
): Promise<PageResult<T>> {
  const res: any = await service.post(url, query, {
    headers: { 'X-Keep-Wrapper': '1' }
  })
  // 兼容后端两种返回：{success, result, totalCount} 或 直接数组
  if (Array.isArray(res)) {
    return { rows: res, total: res.length }
  }
  if (res && Array.isArray(res.result)) {
    return { rows: res.result, total: Number(res.totalCount ?? res.result.length) || 0 }
  }
  return { rows: [], total: 0 }
}

/** 全量导出/下载（旧 downloadFile 的增强版，支持 POST 导出） */
export async function download(url: string, data?: any, filename?: string): Promise<void> {
  const resp: any = await service.post(url, data, { responseType: 'blob' })
  const blob = new Blob([resp.data ?? resp])
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename || `export_${Date.now()}.xlsx`
  link.click()
  URL.revokeObjectURL(link.href)
}

/** 旧 downloadFile(fileId)：通过文件ID下载附件 */
export function downloadFileById(fileId: string): void {
  if (!fileId) return
  window.open(`${API_BASE}/filedownload?fileId=${fileId}`, '_blank')
}

/** 旧 popConfirm 的等价实现 */
export function confirm(message: string, title = '系统确认'): Promise<void> {
  return ElMessageBox.confirm(message, title, {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => undefined)
}

export default service
